import { PersonOfInterest, DriverLicense } from '../types';
import { useState, useEffect } from 'react';

// Add this helper function for formatting dates for input fields
const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  
  try {
    // Handle ISO format with timezone (e.g., "1976-04-14T22:00:00.000Z")
    let date: Date;
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse the date string
    date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Try manual parsing for "DD/MM/YYYY" format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          const yearNum = date.getFullYear();
          const monthNum = String(date.getMonth() + 1).padStart(2, '0');
          const dayNum = String(date.getDate()).padStart(2, '0');
          return `${yearNum}-${monthNum}-${dayNum}`;
        }
      }
      return '';
    }
    
    // Format as YYYY-MM-DD for HTML date input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

interface PersonData {
  name: string;
  surname: string;
  email: string;
  designation: string;
  status: string;
  contact_number?: string;
  id_passport_number?: string;
  date_of_birth?: string;
  drivers_license_codes?: DriverLicense[];
}

interface AddPersonModalProps {
    isOpen: boolean;
    person: Partial<PersonOfInterest>;
    errors: {
    name: string;
    surname: string;
    email: string;
    designation: string;
    status: string;
    contact_number: string;
    id_passport_number: string;
    date_of_birth: string;
    drivers_license_codes: string;
    server: string;
  };
  editingPersonId: number | null;
  usedLicenseCodes?: string[];
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> & { target: { value: string | string[] | DriverLicense[] } }) => void;
  onDriverLicensesChange: (licenses: DriverLicense[]) => void;
}

// Driver License Selector Component - now inside the same file
interface DriverLicenseSelectorProps {
  licenses: DriverLicense[];
  usedLicenseCodes: string[];
  onChange: (licenses: DriverLicense[]) => void;
}

const DriverLicenseSelector: React.FC<DriverLicenseSelectorProps> = ({
  licenses,
  usedLicenseCodes,
  onChange,
}) => {
  const availableLicenseCodes = ['Learners', 'A', 'A1', 'B', 'C', 'C1', 'D', 'D1', 'EB', 'EC', 'EC1', 'ED', 'ED1'];

  const addLicense = () => {
    const currentCodes = licenses.map(l => l.code);
    const availableCode = availableLicenseCodes.find(code => 
      !currentCodes.includes(code) && !usedLicenseCodes.includes(code)
    );
    
    if (availableCode) {
      const newLicense: DriverLicense = {
        code: availableCode,
        dateOfFirstIssue: '',
        expiresOn: ''
      };
      onChange([...licenses, newLicense]);
    }
  };

  const updateLicense = (index: number, field: keyof DriverLicense, value: string) => {
    const updatedLicenses = licenses.map((license, i) => 
      i === index ? { ...license, [field]: value } : license
    );
    onChange(updatedLicenses);
  };

  const removeLicense = (index: number) => {
    const updatedLicenses = licenses.filter((_, i) => i !== index);
    onChange(updatedLicenses);
  };

  const getAvailableLicenseCodes = (currentIndex: number) => {
    const currentSelectedCodes = licenses.map(license => license.code).filter(code => code !== '');
    
    return availableLicenseCodes.filter(code => {
      const selectedInOtherDropdowns = currentSelectedCodes.filter(
        (selectedCode, index) => index !== currentIndex && selectedCode === code
      );
      // Allow 'Learners' to always be available, regardless of usedLicenseCodes
        if (code === 'Learners') {
          return selectedInOtherDropdowns.length === 0;
        }
      return selectedInOtherDropdowns.length === 0 && !usedLicenseCodes.includes(code);
    });
  };

  const canAddMoreLicenses = () => {
    const currentCodes = licenses.map(l => l.code);
    return availableLicenseCodes.some(code => 
      !currentCodes.includes(code) && !usedLicenseCodes.includes(code)
    );
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Driver's License Codes
      </label>
      
      {licenses.length === 0 ? (
        <p className="text-gray-500 text-sm">No license codes added yet.</p>
      ) : (
        licenses.map((license, index) => {
          const availableCodes = getAvailableLicenseCodes(index);
          
          return (
            <div key={index} className="flex gap-2 items-end p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">License Code</label>
                <select
                  value={license.code}
                  onChange={(e) => updateLicense(index, 'code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Code</option>
                  {availableCodes.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                  {license.code && !availableCodes.includes(license.code) && (
                    <option value={license.code} disabled>
                      {license.code} (selected)
                    </option>
                  )}
                </select>
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of First Issue</label>
                <input
                  type="date"
                  value={license.dateOfFirstIssue || ''}
                  onChange={(e) => updateLicense(index, 'dateOfFirstIssue', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Expires On field */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires On</label>
                <input
                  type="date"
                  value={license.expiresOn || ''}
                  onChange={(e) => updateLicense(index, 'expiresOn', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={() => removeLicense(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 h-[42px]"
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })
      )}
      
      <div className="flex justify-start pt-2">
        <button
          type="button"
          onClick={addLicense}
          disabled={!canAddMoreLicenses()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          + Add License
        </button>
      </div>

      {!canAddMoreLicenses() && licenses.length > 0 && (
        <p className="text-sm text-gray-500">All available license codes have been added</p>
      )}
    </div>
  );
};

// Main AddPersonModal Component
const AddPersonModal = ({
  isOpen,
  person,
  errors,
  editingPersonId,
  usedLicenseCodes = [],
  onClose,
  onSubmit,
  onInputChange,
  onDriverLicensesChange,
}: AddPersonModalProps) => {

  if (!isOpen) {
    return null;
  }

  // Parse driver licenses from the person data
  const parseDriverLicenses = (licenseString: string | undefined): DriverLicense[] => {
    if (!licenseString || licenseString.trim() === '') {
      return [];
    }

    try {
      // Parse as pipe-separated format (code|date|expiry,code|date|expiry)
      const licensePairs = licenseString.split(',');
      return licensePairs.map(pair => {
        const [code, dateOfFirstIssue, expiresOn] = pair.split('|');
        return {
          code: code || '',
          dateOfFirstIssue: dateOfFirstIssue || '',
          expiresOn: expiresOn || ''
        };
      }).filter(license => license.code);
    } catch (error) {
      console.error('Error parsing driver licenses:', error);
      return [];
    }
  };

  // Convert driver licenses to array of objects for backend
  const licensesToArray = (licenses: DriverLicense[]): DriverLicense[] => {
      const validLicenses = licenses.filter(license => license.code && license.code.trim() !== '');
      return validLicenses.map(license => ({
        code: license.code,
        dateOfFirstIssue: license.dateOfFirstIssue || '',
        expiresOn: license.expiresOn || ''
      }));
    };

  // Handle driver licenses change and convert to string for parent
  const handleDriverLicensesChange = (licenses: DriverLicense[]) => {
    // Convert to array format for backend
    const licenseArray = licensesToArray(licenses);
    
    // Update parent component
    onDriverLicensesChange(licenseArray);
    
    // Update form data with array (not string)
    const syntheticEvent = {
      target: {
        name: 'drivers_license_codes',
        value: licenseArray,
      },
    } as any; // Use any to bypass type checking for array value
    
    onInputChange(syntheticEvent);
  };

  // Get current driver licenses - handle both array and string formats
const getCurrentDriverLicenses = (): DriverLicense[] => {
  const licenseData = person.drivers_license_codes;
  
  // If it's already an array of DriverLicense objects, return it
  if (Array.isArray(licenseData)) {
    return licenseData;
  }
  
  // If it's undefined or null, return empty array
  if (licenseData == null) {
    return [];
  }
  
  // If it's a string, parse it
  if (typeof licenseData === 'string') {
    // Use String() to ensure we have a string and can call trim()
    const licenseString = String(licenseData);
    
    if (licenseString.trim() === '') {
      return [];
    }
    
    try {
      const licensePairs = licenseString.split(',');
      return licensePairs.map(pair => {
        const [code, dateOfFirstIssue, expiresOn] = pair.split('|');
        return {
          code: code || '',
          dateOfFirstIssue: dateOfFirstIssue || '',
          expiresOn: expiresOn || ''
        };
      }).filter(license => license.code && license.code.trim() !== '');
    } catch (error) {
      console.error('Error parsing driver licenses:', error);
      return [];
    }
  }
  
  // If it's some other type, return empty array
  return [];
};

  // Get current driver licenses from person data
  const currentDriverLicenses = getCurrentDriverLicenses();

  // Validate ID/Personal Code on change
  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits and limit to 13 characters
    if (/^\d{0,13}$/.test(value)) {
      onInputChange(e);
    }
  };

  // Add debug logging to see what's being submitted
  const handleFormSubmit = (e: React.FormEvent) => {
    console.log('🚀 AddPersonModal: Form submission starting');
    console.log('📋 Person data in modal:', person);
    console.log('📋 Current driver licenses:', currentDriverLicenses)
    
    e.preventDefault();
    onSubmit(e);

    console.log('🏁 AddPersonModal: Form submission completed');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-visible">
        <h3 className="text-lg font-semibold mb-4">
          {editingPersonId ? 'Update Person of Interest' : 'Add New Person of Interest'}
        </h3>
        {errors.server && <p className="text-red-500 text-sm mb-4">{errors.server}</p>}

        <form onSubmit={(e) => {
          console.log('📝 STEP 1: Form onSubmit triggered in AddPersonModal');
          e.preventDefault();
          console.log('📝 STEP 2: preventDefault() called');
          console.log('📝 STEP 3: onSubmit prop value:', onSubmit);
          
          try {
            console.log('📝 STEP 4: Entering try block');
            console.log('📝 STEP 5: onSubmit is defined, about to call it');
            onSubmit(e);
            console.log('📝 STEP 6: onSubmit called successfully');
          } catch (error) {
            console.error('📝 STEP 7: Error calling onSubmit:', error);
          } finally {
            console.log('📝 STEP 8: After try-catch block');
          }
        }} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={person.name}
                onChange={onInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                placeholder="e.g. John"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-1">
                Surname
              </label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={person.surname}
                onChange={onInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.surname ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                placeholder="e.g. Doe"
              />
              {errors.surname && <p className="text-red-500 text-xs mt-1">{errors.surname}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={person.email}
                onChange={onInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                placeholder="e.g. john.doe@example.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-1">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={person.designation}
                onChange={onInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.designation ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                placeholder="e.g. Manager"
              />
              {errors.designation && <p className="text-red-500 text-xs mt-1">{errors.designation}</p>}
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={person.status}
                onChange={onInputChange}
                className={`w-full px-3 py-2 border ${
                  errors.status ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500`}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {errors.status && <p className="text-red-500 text-xs mt-1">{errors.status}</p>}
            </div>

            <div>
              <label htmlFor="contact_number" className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number
              </label>
              <input
                type="text"
                id="contact_number"
                name="contact_number"
                value={person.contact_number || ''}
                onChange={onInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., +27831234567 (9 digits after +27)"
              />
              {errors.contact_number && (
                <p className="text-red-500 text-xs mt-1">{errors.contact_number}</p>
              )}
            </div>

            <div>
              <label htmlFor="id_passport_number" className="block text-sm font-medium text-gray-700 mb-1">
                ID/Passport Number
              </label>
              <input
                type="number"
                id="id_passport_number"
                name="id_passport_number"
                value={person.id_passport_number || ''}
                onChange={handleIdChange}
                maxLength={13}
                className={`w-full px-3 py-2 border ${
                  errors.id_passport_number ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                placeholder="Enter ID or Passport Number"
              />
              {errors.id_passport_number && (
                <p className="text-red-500 text-xs mt-1">{errors.id_passport_number}</p>
              )}
            </div>

            <div>
              <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                value={formatDateForInput(person.date_of_birth)}
                onChange={(e) => {
                  const syntheticEvent = {
                    target: {
                      name: 'date_of_birth',
                      value: e.target.value
                    }
                  } as React.ChangeEvent<HTMLInputElement>;
                  onInputChange(syntheticEvent);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {errors.date_of_birth && (
                <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
              )}
            </div>

            {/* Driver's License Section */}
            <div className="mb-4">
              <DriverLicenseSelector
                licenses={currentDriverLicenses}
                usedLicenseCodes={usedLicenseCodes}
                onChange={handleDriverLicensesChange}
              />
              {errors.drivers_license_codes && (
                <p className="text-red-500 text-sm mt-1">{errors.drivers_license_codes}</p>
              )}
            </div>

          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              {editingPersonId ? 'Update' : 'Add Person'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPersonModal;